/**
 * Search Suggest Handler Module
 * Handles user interactions with search suggestions
 */

import type { SearchSuggestApi } from "./search-suggest-api";
import { searchSuggestRender } from "./search-suggest-render";
import { SearchSuggestListener } from "./search-suggest-listener";
import type { SearchSuggestValue } from "./search-suggest-api";
import { searchSuggestForm } from "./search-suggest-form";

export type SearchSuggestOptions = {
    minChars?: number;
    searchType?: string;
    sectionId?: string;
    boardId?: string;
    excludeTypes?: string[];
    limit?: number;
    autoSubmit?: boolean;
    [key: string]: SearchSuggestValue;
};

type SuggestSelectDetail = {
    value?: string;
    text?: string;
    type?: string;
    outputField?: string;
    outputValue?: string;
};

class SearchSuggestHandler {
    public api: SearchSuggestApi;

    public render: typeof searchSuggestRender;

    public activeIndex: number;

    public currentDropdown: HTMLElement | null;

    public debounceTimer: ReturnType<typeof setTimeout> | null;

    public debounceDelay: number;

    /**
     * Latest query requested per dropdown, so a slow/out-of-order response
     * for an older keystroke can't overwrite a newer, already-rendered one.
     * The `/api/v1/search/suggests` endpoint alone can take over a second
     * (it re-reads/parses the full suggestions snapshot file per request —
     * see ISS-04-102), so two searches fired ~300ms apart (a normal typing
     * pace) can easily resolve out of order. Without this guard, whichever
     * response lands last "wins" the render regardless of which query is
     * still current — including an older, narrower query resolving with
     * zero matches and silently blanking out a dropdown that already
     * correctly showed the newer query's results.
     */
    private latestQueryByDropdown = new WeakMap<HTMLElement, string>();

    public constructor(api: SearchSuggestApi) {
        this.api = api;
        this.render = searchSuggestRender;
        this.activeIndex = -1;
        this.currentDropdown = null;
        this.debounceTimer = null;
        this.debounceDelay = 300;
    }

    public initialize(inputElement: HTMLElement, options: SearchSuggestOptions = {}): void {
        if (inputElement.dataset.searchSuggestBound === "1") return;

        const dropdown = this.render.createDropdown(inputElement);
        inputElement.dataset.suggestOptions = JSON.stringify(options);
        SearchSuggestListener.setup(inputElement, dropdown, this, options);
    }

    public handleInput(event: Event, dropdown: HTMLElement, options: SearchSuggestOptions): void {
        if (!(event.target instanceof HTMLInputElement)) return;
        const input = event.target;
        const query = input.value.trim();

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        if (query.length < (options.minChars || 2)) {
            this.render.hideDropdown(dropdown);
            return;
        }

        this.debounceTimer = setTimeout(async () => {
            await this.performSearch(query, dropdown, options);
        }, this.debounceDelay);
    }

    public showLoadingInDropdown(dropdown: HTMLElement): void {
        this.render.renderLoading(dropdown);
    }

    public async performSearch(query: string, dropdown: HTMLElement, options: SearchSuggestOptions): Promise<void> {
        this.latestQueryByDropdown.set(dropdown, query);
        this.showLoadingInDropdown(dropdown);

        try {
            const suggestions = await this.api.searchSuggestions(query, {
                searchType: options.searchType,
                sectionId: options.sectionId,
                boardId: typeof options.boardId === "string" ? options.boardId : undefined,
                excludeTypes: Array.isArray(options.excludeTypes) ? options.excludeTypes : undefined,
                limit: options.limit || 10,
            });

            // A newer search for this same dropdown was issued while this one
            // was in flight — its response (already rendered, or still
            // pending) is the one that should stay on screen, not this stale
            // one, however it happens to resolve.
            if (this.latestQueryByDropdown.get(dropdown) !== query) {
                return;
            }

            this.render.renderSuggestions(dropdown, suggestions, query);
            this.activeIndex = -1;
            this.currentDropdown = dropdown;
        } catch (error) {
            if (this.latestQueryByDropdown.get(dropdown) !== query) {
                return;
            }
            console.error("Failed to fetch suggestions:", error);
            this.render.renderError(dropdown);
        }
    }

    public handleKeydown(event: KeyboardEvent, inputElement: HTMLElement, dropdown: HTMLElement): void {
        const items = searchSuggestForm.getItems(dropdown);

        if (items.length === 0) return;

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
                this.render.setActiveItem(dropdown, this.activeIndex);
                break;

            case "ArrowUp":
                event.preventDefault();
                this.activeIndex = Math.max(this.activeIndex - 1, -1);
                if (this.activeIndex === -1) {
                    items.forEach((item) => item.classList.remove("active"));
                } else {
                    this.render.setActiveItem(dropdown, this.activeIndex);
                }
                break;

            case "Enter":
                event.preventDefault();
                if (this.activeIndex >= 0) {
                    const activeItem = items[this.activeIndex];
                    if (activeItem instanceof HTMLElement) {
                        this.selectItem(activeItem, inputElement, dropdown);
                    }
                }
                break;

            case "Escape":
                this.render.hideDropdown(dropdown);
                this.activeIndex = -1;
                break;
        }
    }

    public handleItemClick(
        event: Event,
        inputElement: HTMLElement,
        dropdown: HTMLElement,
        options: SearchSuggestOptions,
    ): void {
        if (!(event.target instanceof Element)) return;
        const item = searchSuggestForm.getClosestItem(event.target);
        if (!(item instanceof HTMLElement)) return;

        event.preventDefault();
        this.selectItem(item, inputElement, dropdown, options);
    }

    public selectItem(
        item: HTMLElement,
        inputElement: HTMLElement,
        dropdown: HTMLElement,
        options: SearchSuggestOptions = {},
    ): void {
        const value = item.dataset.value;
        const text = searchSuggestForm.getItemText(item)?.textContent || value || "";
        const type = item.dataset.type;
        const outputField = item.dataset.outputField;
        const outputValue = item.dataset.outputValue;

        if (inputElement instanceof HTMLInputElement) {
            inputElement.value = text;
        }

        this.render.hideDropdown(dropdown);
        this.activeIndex = -1;

        const selectEvent = new CustomEvent<SuggestSelectDetail>("suggest:select", {
            detail: {
                value,
                text,
                type,
                outputField,
                outputValue,
            },
        });
        inputElement.dispatchEvent(selectEvent);

        if (options.autoSubmit !== false) {
            const form = searchSuggestForm.getClosestForm(inputElement);
            if (form) {
                form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
            }
        }
    }
}

export { SearchSuggestHandler };
